import { apiFetch } from "../utils/api";

export interface ExtractNationalIdFrontRequest {
  Data: string; // Base64 string with data:image/png;base64, prefix
  DocumentName: string;
}

export interface ExtractNationalIdBackRequest {
  Data: string; // Base64 string with data:image/png;base64, prefix
  DocumentName: string;
}

export interface ExtractDrivingLicenseRequest {
  Data: string; // Base64 string with data:image/png;base64, prefix
  DocumentName: string;
}

export interface ExtractVehicleLicenseFrontRequest {
  Data: string; // Base64 string with data:image/png;base64, prefix
  DocumentName: string;
}

export interface ExtractVehicleLicenseBackRequest {
  Data: string; // Base64 string with data:image/png;base64, prefix
  DocumentName: string;
  VehicleMake?: string;
  VehicleModel?: string;
}

export interface GeminiExtractDataResponse {
  fullName: string;
  address: string;
  nationalIdNumber: string;
  cardSerialNumber: string;
  birthDate: string;
  attachmentRelativePath: string;
  success: boolean;
  statusCode: number;
  errorMessage: string;
}

export interface GeminiExtractNationalIdBackResponse {
  nationaIdNumber: string; // Note: typo in backend (NationaIdNumber)
  gender: string;
  religion: string;
  maritalStatus: string;
  professionQualification: string;
  dateOfIssue: string;
  dateOfExpiry: string;
  attachmentRelativePath: string;
  success: boolean;
  statusCode: number;
  errorMessage: string;
}

export interface GeminiExtractDrivingLicenseResponse {
  issuingAuthority: string;
  issuingOffice: string;
  licenseType: string;
  nationalId: string;
  nameArabic: string;
  nameEnglish: string;
  address: string;
  nationalityArabic: string;
  nationalityEnglish: string;
  occupation: string;
  dateOfIssue: string;
  dateOfExpiry: string;
  licenseClass: string;
  attachmentRelativePath: string;
  success: boolean;
  statusCode: number;
  errorMessage: string;
}

export interface GeminiExtractVehicleLicenseFrontResponse {
  issuingTrafficDepartment: string;
  issuingTrafficUnit: string;
  ownerName: string;
  ownerAddress: string;
  ownerNationality: string;
  licenseType: string;
  licenseIssueDate: string;
  licenseExpiryDate: string;
  vehiclePlateNumber: string;
  attachmentRelativePath: string;
  success: boolean;
  statusCode: number;
  errorMessage: string;
}

export interface GeminiExtractVehicleLicenseBackResponse {
  make: string;
  model: string;
  chassisNumber: string;
  motorNumber: string;
  engineCylinders: string;
  fuelType: string;
  engineCapacity: string;
  features: string;
  code: string;
  modelYear: string;
  bodyStyle: string;
  inspectionExpiryYear: string;
  vehicleColor: string;
  attachmentRelativePath: string;
  makeModelMatchResult?: string;
  success: boolean;
  statusCode: number;
  errorMessage: string;
}

/**
 * Extracts data from National ID front image using backend API
 * @param base64Image - Base64 image string (with or without data:image prefix)
 * @param documentName - Optional document name
 * @returns Extracted data from the National ID front
 */
export const extractNationalIdFront = async (
  base64Image: string,
  documentName: string = "national_id_front.jpg"
): Promise<GeminiExtractDataResponse> => {
  // Ensure base64 format is data:image/png;base64,{base64string}
  let formattedBase64 = base64Image;
  
  // If it already has the data:image prefix, use it as-is
  if (!base64Image.startsWith("data:image/")) {
    // If it's just the base64 string, add the prefix
    formattedBase64 = `data:image/png;base64,${base64Image}`;
  } else {
    // If it has a different format like data:image/jpeg, convert to png format
    if (!base64Image.startsWith("data:image/png;base64,")) {
      // Extract just the base64 part
      const base64Part = base64Image.includes(",") 
        ? base64Image.split(",")[1] 
        : base64Image.replace(/^data:image\/[^;]+;base64,/, "");
      formattedBase64 = `data:image/png;base64,${base64Part}`;
    }
  }

  const request: ExtractNationalIdFrontRequest = {
    Data: formattedBase64,
    DocumentName: documentName,
  };

  const response = await apiFetch("/Gemini/extract-national-id-front", {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while extracting National ID front: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    
    // Check if this is a photo validation error (402 status code)
    if (response.status === 402) {
      const validationError = new Error(errorMessage);
      (validationError as any).isPhotoValidationError = true;
      (validationError as any).documentType = "National ID front";
      throw validationError;
    }
    
    throw new Error(errorMessage);
  }

  const data = await response.json();
  
  // Map backend response to our interface (handle case sensitivity)
  return {
    fullName: data.FullName || data.fullName || "",
    address: data.Address || data.address || "",
    nationalIdNumber: data.NationalIdNumber || data.nationalIdNumber || "",
    cardSerialNumber: data.CardSerialNumber || data.cardSerialNumber || "",
    birthDate: data.BirthDate || data.birthDate || "",
    attachmentRelativePath: data.AttachmentRelativePath || data.attachmentRelativePath || "",
    success: data.Success !== undefined ? data.Success : data.success !== undefined ? data.success : true,
    statusCode: data.StatusCode || data.statusCode || response.status,
    errorMessage: data.ErrorMessage || data.errorMessage || "",
  };
};

/**
 * Extracts data from National ID back image using backend API
 * @param base64Image - Base64 image string (with or without data:image prefix)
 * @param documentName - Optional document name
 * @returns Extracted data from the National ID back
 */
export const extractNationalIdBack = async (
  base64Image: string,
  documentName: string = "national_id_back.jpg"
): Promise<GeminiExtractNationalIdBackResponse> => {
  // Ensure base64 format is data:image/png;base64,{base64string}
  let formattedBase64 = base64Image;
  
  // If it already has the data:image prefix, use it as-is
  if (!base64Image.startsWith("data:image/")) {
    // If it's just the base64 string, add the prefix
    formattedBase64 = `data:image/png;base64,${base64Image}`;
  } else {
    // If it has a different format like data:image/jpeg, convert to png format
    if (!base64Image.startsWith("data:image/png;base64,")) {
      // Extract just the base64 part
      const base64Part = base64Image.includes(",") 
        ? base64Image.split(",")[1] 
        : base64Image.replace(/^data:image\/[^;]+;base64,/, "");
      formattedBase64 = `data:image/png;base64,${base64Part}`;
    }
  }

  const request: ExtractNationalIdBackRequest = {
    Data: formattedBase64,
    DocumentName: documentName,
  };

  const response = await apiFetch("/Gemini/extract-national-id-back", {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while extracting National ID back: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    
    // Check if this is a photo validation error (402 status code)
    if (response.status === 402) {
      const validationError = new Error(errorMessage);
      (validationError as any).isPhotoValidationError = true;
      (validationError as any).documentType = "National ID back";
      throw validationError;
    }
    
    throw new Error(errorMessage);
  }

  const data = await response.json();
  
  // Map backend response to our interface (handle case sensitivity)
  // Note: Backend has typo "NationaIdNumber" instead of "NationalIdNumber"
  return {
    nationaIdNumber: data.NationaIdNumber || data.nationaIdNumber || "",
    gender: data.Gender || data.gender || "",
    religion: data.Religion || data.religion || "",
    maritalStatus: data.MaritalStatus || data.maritalStatus || "",
    professionQualification: data.ProfessionQualification || data.professionQualification || "",
    dateOfIssue: data.DateOfIssue || data.dateOfIssue || "",
    dateOfExpiry: data.DateOfExpiry || data.dateOfExpiry || "",
    attachmentRelativePath: data.AttachmentRelativePath || data.attachmentRelativePath || "",
    success: data.Success !== undefined ? data.Success : data.success !== undefined ? data.success : true,
    statusCode: data.StatusCode || data.statusCode || response.status,
    errorMessage: data.ErrorMessage || data.errorMessage || "",
  };
};

/**
 * Helper function to format base64 image to data:image/png;base64,{base64string}
 */
const formatBase64Image = (base64Image: string): string => {
  let formattedBase64 = base64Image;
  
  if (!base64Image.startsWith("data:image/")) {
    formattedBase64 = `data:image/png;base64,${base64Image}`;
  } else {
    if (!base64Image.startsWith("data:image/png;base64,")) {
      const base64Part = base64Image.includes(",") 
        ? base64Image.split(",")[1] 
        : base64Image.replace(/^data:image\/[^;]+;base64,/, "");
      formattedBase64 = `data:image/png;base64,${base64Part}`;
    }
  }
  
  return formattedBase64;
};

/**
 * Extracts data from Driving License image using backend API
 * @param base64Image - Base64 image string (with or without data:image prefix)
 * @param documentName - Optional document name
 * @returns Extracted data from the Driving License
 */
export const extractDrivingLicense = async (
  base64Image: string,
  documentName: string = "driving_license.jpg"
): Promise<GeminiExtractDrivingLicenseResponse> => {
  const formattedBase64 = formatBase64Image(base64Image);

  const request: ExtractDrivingLicenseRequest = {
    Data: formattedBase64,
    DocumentName: documentName,
  };

  const response = await apiFetch("/Gemini/extract-driving-license", {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while extracting Driving License: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    
    // Check if this is a photo validation error (402 status code)
    if (response.status === 402) {
      const validationError = new Error(errorMessage);
      (validationError as any).isPhotoValidationError = true;
      (validationError as any).documentType = "Driving License";
      throw validationError;
    }
    
    throw new Error(errorMessage);
  }

  const data = await response.json();
  
  return {
    issuingAuthority: data.IssuingAuthority || data.issuingAuthority || "",
    issuingOffice: data.IssuingOffice || data.issuingOffice || "",
    licenseType: data.LicenseType || data.licenseType || "",
    nationalId: data.NationalId || data.nationalId || "",
    nameArabic: data.NameArabic || data.nameArabic || "",
    nameEnglish: data.NameEnglish || data.nameEnglish || "",
    address: data.Address || data.address || "",
    nationalityArabic: data.NationalityArabic || data.nationalityArabic || "",
    nationalityEnglish: data.NationalityEnglish || data.nationalityEnglish || "",
    occupation: data.Occupation || data.occupation || "",
    dateOfIssue: data.DateOfIssue || data.dateOfIssue || "",
    dateOfExpiry: data.DateOfExpiry || data.dateOfExpiry || "",
    licenseClass: data.LicenseClass || data.licenseClass || "",
    attachmentRelativePath: data.AttachmentRelativePath || data.attachmentRelativePath || "",
    success: data.Success !== undefined ? data.Success : data.success !== undefined ? data.success : true,
    statusCode: data.StatusCode || data.statusCode || response.status,
    errorMessage: data.ErrorMessage || data.errorMessage || "",
  };
};

/**
 * Extracts data from Vehicle License front image using backend API
 * @param base64Image - Base64 image string (with or without data:image prefix)
 * @param documentName - Optional document name
 * @returns Extracted data from the Vehicle License front
 */
export const extractVehicleLicenseFront = async (
  base64Image: string,
  documentName: string = "vehicle_license_front.jpg"
): Promise<GeminiExtractVehicleLicenseFrontResponse> => {
  const formattedBase64 = formatBase64Image(base64Image);

  const request: ExtractVehicleLicenseFrontRequest = {
    Data: formattedBase64,
    DocumentName: documentName,
  };

  const response = await apiFetch("/Gemini/extract-vehicle-license-front", {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while extracting Vehicle License front: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    
    // Check if this is a photo validation error (402 status code)
    if (response.status === 402) {
      const validationError = new Error(errorMessage);
      (validationError as any).isPhotoValidationError = true;
      (validationError as any).documentType = "Vehicle License front";
      throw validationError;
    }
    
    throw new Error(errorMessage);
  }

  const data = await response.json();
  
  return {
    issuingTrafficDepartment: data.IssuingTrafficDepartment || data.issuingTrafficDepartment || "",
    issuingTrafficUnit: data.IssuingTrafficUnit || data.issuingTrafficUnit || "",
    ownerName: data.OwnerName || data.ownerName || "",
    ownerAddress: data.OwnerAddress || data.ownerAddress || "",
    ownerNationality: data.OwnerNationality || data.ownerNationality || "",
    licenseType: data.LicenseType || data.licenseType || "",
    licenseIssueDate: data.LicenseIssueDate || data.licenseIssueDate || "",
    licenseExpiryDate: data.LicenseExpiryDate || data.licenseExpiryDate || "",
    vehiclePlateNumber: data.VehiclePlateNumber || data.vehiclePlateNumber || "",
    attachmentRelativePath: data.AttachmentRelativePath || data.attachmentRelativePath || "",
    success: data.Success !== undefined ? data.Success : data.success !== undefined ? data.success : true,
    statusCode: data.StatusCode || data.statusCode || response.status,
    errorMessage: data.ErrorMessage || data.errorMessage || "",
  };
};

/**
 * Extracts data from Vehicle License back image using backend API
 * @param base64Image - Base64 image string (with or without data:image prefix)
 * @param documentName - Optional document name
 * @returns Extracted data from the Vehicle License back
 */
export const extractVehicleLicenseBack = async (
  base64Image: string,
  documentName: string = "vehicle_license_back.jpg",
  vehicleMake?: string,
  vehicleModel?: string
): Promise<GeminiExtractVehicleLicenseBackResponse> => {
  const formattedBase64 = formatBase64Image(base64Image);

  const request: ExtractVehicleLicenseBackRequest = {
    Data: formattedBase64,
    DocumentName: documentName,
    ...(vehicleMake ? { VehicleMake: vehicleMake } : {}),
    ...(vehicleModel ? { VehicleModel: vehicleModel } : {}),
  };

  const response = await apiFetch("/Gemini/extract-vehicle-license-back", {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while extracting Vehicle License back: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    
    // Check if this is a photo validation error (402 status code)
    if (response.status === 402) {
      const validationError = new Error(errorMessage);
      (validationError as any).isPhotoValidationError = true;
      (validationError as any).documentType = "Vehicle License back";
      throw validationError;
    }
    
    throw new Error(errorMessage);
  }

  const data = await response.json();
  
  return {
    make: data.Make || data.make || "",
    model: data.Model || data.model || "",
    chassisNumber: data.ChassisNumber || data.chassisNumber || "",
    motorNumber: data.MotorNumber || data.motorNumber || "",
    engineCylinders: data.EngineCylinders || data.engineCylinders || "",
    fuelType: data.FuelType || data.fuelType || "",
    engineCapacity: data.EngineCapacity || data.engineCapacity || "",
    features: data.Features || data.features || "",
    code: data.Code || data.code || "",
    modelYear: data.ModelYear || data.modelYear || "",
    bodyStyle: data.BodyStyle || data.bodyStyle || "",
    inspectionExpiryYear: data.InspectionExpiryYear || data.inspectionExpiryYear || "",
    vehicleColor: data.VehicleColor || data.vehicleColor || "",
    attachmentRelativePath: data.AttachmentRelativePath || data.attachmentRelativePath || "",
    makeModelMatchResult: data.MakeModelMatchResult || data.makeModelMatchResult || undefined,
    success: data.Success !== undefined ? data.Success : data.success !== undefined ? data.success : true,
    statusCode: data.StatusCode || data.statusCode || response.status,
    errorMessage: data.ErrorMessage || data.errorMessage || "",
  };
};

